import styled, { css, keyframes } from 'styled-components';
import { EDITOR_TOPBAR_HEIGHT } from '../editorLayout.constants';

const slideIn = keyframes`
  from {
    transform: translateX(-12px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(-12px);
    opacity: 0;
  }
`;

interface FlyoutProps {
  $open: boolean;
}

/**
 * The flyout dock — anchored to the rail's right edge so the panel slides
 * out from behind the rail (matching the prototype's `.ops-panel`). The
 * Sidebar slot in `BaseLayout` is the positioned ancestor (it carries
 * `position: relative`), so `left: 100%` puts the flyout flush at 72px
 * from the viewport edge.
 *
 * Vertically the panel docks UNDER the editor's topbar (matching the
 * prototype's `.ops-panel { top: 60px }` rule and our right-side
 * {@link DockRoot}). The topbar lives in the `Main` column of the
 * BaseLayout grid — not the Sidebar column — so we offset by
 * {@link EDITOR_TOPBAR_HEIGHT} to align with the bottom edge of the
 * topbar's border, leaving the topbar fully clickable when the flyout
 * is open.
 *
 * `pointer-events: none` while closed keeps the (still-mounted) panel
 * from intercepting clicks on the canvas. We keep it mounted across
 * open/close so the slide-in animation doesn't fight a remount, and so
 * search / filter state is preserved when the user toggles the flyout.
 */
export const FlyoutRoot = styled.aside<FlyoutProps>`
  position: absolute;
  top: ${EDITOR_TOPBAR_HEIGHT}px;
  bottom: 0;
  left: 100%;
  width: 320px;
  z-index: 30;
  background: ${({ theme: { colors = {} } }) => colors.surface ?? '#0E0F18'};
  border-right: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  box-shadow: 8px 0 32px -16px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  isolation: isolate;

  ${({ $open }) =>
    $open
      ? css`
          transform: translateX(0);
          opacity: 1;
          pointer-events: auto;
          visibility: visible;
          animation: ${slideIn} 220ms cubic-bezier(0.4, 0, 0.2, 1);
        `
      : css`
          transform: translateX(-12px);
          opacity: 0;
          pointer-events: none;
          visibility: hidden;
          animation: ${slideOut} 180ms ease;
        `}

  /* On mobile (sidebar fully overlays via the BaseLayout's mobile drawer)
     the flyout sits flush against the left edge of the viewport since
     the rail itself is the drawer. */
  @media (max-width: 768px) {
    left: 0;
  }
`;
